import { useState, useEffect } from 'react';
import { Search } from 'lucide-react';
import { DocumentUpload } from './DocumentUpload';
import { DocumentList } from './DocumentList';
import { DOCUMENT_TYPES } from '@/types/document';
import type { Document, DocumentType } from '@/types/document';
import { getStoredDocuments, addStoredDocument, deleteStoredDocument, subscribeToDocumentUpdates } from '../../../../utils/documentStorage';

export function DocumentManagement() {
  const [documents, setDocuments] = useState<Document[]>(() => getStoredDocuments());
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<DocumentType | 'all'>('all');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return subscribeToDocumentUpdates(setDocuments);
  }, []);

  const handleFileSelect = async (files: FileList) => {
    setError(null);
    
    for (const file of Array.from(files)) {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        setError('File size exceeds 10MB limit');
        continue;
      }

      if (!['application/pdf', 'image/jpeg', 'image/png'].includes(file.type)) {
        setError('Invalid file type. Only PDF, JPG, and PNG files are allowed');
        continue;
      }

      // In a real application, we would upload the file to a server here
      // For this demo, we'll create a mock URL
      const mockUrl = `https://example.com/documents/${file.name}`;
      
      const newDocument: Document = {
        id: crypto.randomUUID(),
        name: file.name,
        type: 'other',
        url: mockUrl,
        size: file.size,
        mimeType: file.type,
        uploadDate: new Date().toISOString(),
        uploadedBy: 'current-user',
      };

      addStoredDocument(newDocument);
    }
  };

  const handleDelete = (documentId: string) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      deleteStoredDocument(documentId);
    }
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = 
      doc.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || doc.type === typeFilter;
    return matchesSearch && matchesType;
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Supporting Documents</h2>
      </div>
      
      <DocumentUpload onFileSelect={handleFileSelect} />
      
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{error}</h3>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search documents..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
        </div>
        <select
          className="w-full sm:w-48 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as DocumentType | 'all')}
        >
          <option value="all">All Types</option>
          {DOCUMENT_TYPES.map(type => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
      </div>

      <DocumentList
        documents={filteredDocuments}
        onDelete={handleDelete}
      />
    </div>
  );
}